import { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import { format } from 'date-fns';
import { graphql } from '@/gql';
import { Button } from '@/components/ui/Button';
import { FormField } from '@/components/ui/FormLayout';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { Switch } from '@/components/ui/Switch';
import { graphqlErrorMessage, graphqlErrorReason } from '@/lib/graphqlError';
import { DOORS_WINDOW_REQUIRED, doorsReasonLabel } from '@/lib/labels';
import { doorsWindowProblem, projectDoorsWindow } from './doorsWindow';

const BlueprintsForCreateDoc = graphql(/* GraphQL */ `
  query BlueprintsForCreateNight {
    blueprints(
      filter: { isActive: true }
      page: 1
      pageSize: 100
      sort: { field: TITLE, direction: ASC }
    ) {
      nodes {
        id
        title
        requireTicket
        doorsOpen
        doorsClose
      }
    }
  }
`);

const CreateNightDoc = graphql(/* GraphQL */ `
  mutation CreateNight($input: CreateNightInput!) {
    createNight(input: $input) {
      id
    }
  }
`);

export function CreateNightModal({
  open,
  startsAt,
  onClose,
  onCreated,
}: {
  open: boolean;
  startsAt: Date | null;
  onClose: () => void;
  onCreated: (id: string) => void | Promise<void>;
}) {
  const blueprintsQuery = useQuery(BlueprintsForCreateDoc, { skip: !open, errorPolicy: 'all' });
  const [create, { loading }] = useMutation(CreateNightDoc);
  const [blueprintId, setBlueprintId] = useState('');
  const [title, setTitle] = useState('');
  const [requireTicket, setRequireTicket] = useState(false);
  const [day, setDay] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [seeded, setSeeded] = useState<string | null>(null);
  const [doorsOpen, setDoorsOpen] = useState('');
  const [doorsClose, setDoorsClose] = useState('');
  // Once the manager edits a doors input, picking another blueprint or date
  // no longer rewrites it.
  const [doorsEdited, setDoorsEdited] = useState(false);
  const [showDoorsError, setShowDoorsError] = useState(false);

  const dayKey = startsAt ? format(startsAt, 'yyyy-MM-dd') : '';
  if (open && dayKey && seeded !== dayKey) {
    setSeeded(dayKey);
    setDay(dayKey);
    setBlueprintId('');
    setTitle('');
    setRequireTicket(false);
    setDoorsOpen('');
    setDoorsClose('');
    setDoorsEdited(false);
    setShowDoorsError(false);
    setError(null);
  }

  const empty = blueprintId === '';
  const blueprints = blueprintsQuery.data?.blueprints.nodes ?? [];
  const doorsProblem = doorsWindowProblem(doorsOpen, doorsClose);

  // A blueprint's HH:MM defaults land on the picked date; no blueprint leaves
  // the window for the manager to fill in.
  const prefillDoors = (nextBlueprintId: string, nextDay: string) => {
    if (doorsEdited) return;
    const blueprint = blueprints.find((bp) => bp.id === nextBlueprintId);
    const window = blueprint
      ? projectDoorsWindow(blueprint.doorsOpen, blueprint.doorsClose, nextDay)
      : { open: '', close: '' };
    setDoorsOpen(window.open);
    setDoorsClose(window.close);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add night"
      description="Choose a blueprint to snapshot, or start empty. The day is pre-filled from the day you clicked. A blueprint has no date; this night is the dated night, and its events are added on its page."
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button
            id="admin.nights.create"
            type="button"
            loading={loading}
            onClick={async () => {
              setError(null);
              if (!day) {
                setError('Set the day.');
                return;
              }
              if (empty && !title.trim()) {
                setError('Empty nights need a title.');
                return;
              }
              if (doorsProblem) {
                setShowDoorsError(true);
                return;
              }
              try {
                const result = await create({
                  variables: {
                    input: {
                      blueprintId: blueprintId || null,
                      title: empty ? title.trim() : null,
                      requireTicket: empty ? requireTicket : null,
                      doorsOpen: new Date(doorsOpen).toISOString(),
                      doorsClose: new Date(doorsClose).toISOString(),
                    },
                  },
                });
                const id = result.data?.createNight.id;
                if (id) await onCreated(id);
              } catch (err) {
                const reason = doorsReasonLabel(graphqlErrorReason(err));
                setError(reason ?? graphqlErrorMessage(err));
              }
            }}
          >
            Create
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        <FormField
          label="From blueprint"
          description="Snapshot copies title, doors defaults, stages and ticket text. Later blueprint edits do not rewrite this night."
        >
          <Select
            value={blueprintId}
            onChange={(e) => {
              setBlueprintId(e.target.value);
              prefillDoors(e.target.value, day);
            }}
            options={[
              { value: '', label: 'Empty night (no blueprint)' },
              ...blueprints.map((bp) => ({
                value: bp.id,
                label: bp.title,
              })),
            ]}
          />
        </FormField>
        <FormField label="Day" required description="Doors default to the blueprint's times on this day.">
          <Input
            id="admin.nights.day"
            type="date"
            value={day}
            onChange={(e) => {
              setDay(e.target.value);
              prefillDoors(blueprintId, e.target.value);
            }}
            required
          />
        </FormField>
        <FormField
          label="Doors open"
          required
          description={blueprintId ? 'Prefilled from the blueprint on the day above.' : undefined}
          error={showDoorsError && !doorsOpen ? DOORS_WINDOW_REQUIRED : undefined}
        >
          <Input
            id="admin.nights.create.doorsOpen"
            type="datetime-local"
            value={doorsOpen}
            onChange={(e) => {
              setDoorsOpen(e.target.value);
              setDoorsEdited(true);
            }}
            required
          />
        </FormField>
        <FormField
          label="Doors close"
          required
          error={showDoorsError && doorsOpen ? doorsProblem ?? undefined : undefined}
        >
          <Input
            id="admin.nights.create.doorsClose"
            type="datetime-local"
            value={doorsClose}
            onChange={(e) => {
              setDoorsClose(e.target.value);
              setDoorsEdited(true);
            }}
            required
          />
        </FormField>
        {empty ? (
          <>
            <FormField label="Title" required>
              <Input
                id="admin.nights.title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </FormField>
            <Switch
              id="admin.nights.requireTicket"
              label="Require ticket"
              checked={requireTicket}
              onChange={(e) => setRequireTicket(e.target.checked)}
            />
          </>
        ) : null}
        {error ? <p className="text-sm text-red-600">{error}</p> : null}
      </div>
    </Modal>
  );
}
